import { useEffect } from "react";

const SELECTOR =
  ".scroll-reveal, .scroll-reveal-left, .scroll-reveal-right, .scroll-reveal-scale";

/**
 * Reveals elements as they scroll into view.
 *
 * Fails open. These classes start at opacity: 0, so anything this hook misses stays
 * invisible for good — that is a far worse outcome than an entrance animation that
 * does not play. Content is therefore revealed immediately when it is already at or
 * above the fold, when the viewer prefers reduced motion, and when
 * IntersectionObserver is unavailable.
 *
 * A MutationObserver picks up elements mounted after the initial scan. Without it,
 * anything React renders later — filtering the portfolio grid, for instance — is
 * never observed and never revealed.
 */
export const useScrollReveal = () => {
  useEffect(() => {
    const reveal = (el: Element) => el.classList.add("revealed");
    const all = () => Array.from(document.querySelectorAll(SELECTOR));

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduceMotion || typeof IntersectionObserver === "undefined") {
      all().forEach(reveal);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const observe = (el: Element) => {
      if (el.classList.contains("revealed")) return;
      // Already on screen, or scrolled past: there is nothing to wait for.
      if (el.getBoundingClientRect().top < window.innerHeight) {
        reveal(el);
        return;
      }
      observer.observe(el);
    };

    all().forEach(observe);

    // IntersectionObserver delivery is tied to rendering steps, and an instant jump —
    // an anchor link to #portfolio, or scrollIntoView — can land without producing one.
    // This sweep reveals whatever is on screen regardless of when the observer fires.
    let frame: number | null = null;
    let trailing: ReturnType<typeof setTimeout> | null = null;
    const sweep = () => {
      frame = null;
      all().forEach((el) => {
        if (el.classList.contains("revealed")) return;
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) reveal(el);
      });
    };
    const scheduleSweep = () => {
      if (frame === null) frame = requestAnimationFrame(sweep);
      // One frame is not always enough: images and fonts can still be settling, so a
      // card that measured below the fold on that frame ends up on screen a moment
      // later with nothing left to trigger it. The trailing pass catches those.
      // Deliberately not reset on each call. Debouncing it starves the sweep under
      // continuous scrolling, which is exactly when content is arriving on screen.
      if (trailing === null) {
        trailing = setTimeout(() => {
          trailing = null;
          sweep();
        }, 250);
      }
    };

    scheduleSweep();
    window.addEventListener("scroll", scheduleSweep, { passive: true });
    window.addEventListener("resize", scheduleSweep, { passive: true });
    window.addEventListener("hashchange", scheduleSweep);

    const mutations = new MutationObserver((records) => {
      let changed = false;
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(SELECTOR)) observe(node);
          node.querySelectorAll(SELECTOR).forEach(observe);
          changed = true;
        });
        // Removals matter as much as additions. Filtering the portfolio grid removes
        // cards rather than adding any, and the ones that stay reflow upward into
        // view. Watching additions alone leaves those sitting at opacity 0.
        if (record.removedNodes.length) changed = true;
      }
      // This callback runs before the browser has laid the change out, so rects are
      // not yet trustworthy here. The sweep runs in a frame, after layout.
      if (changed) scheduleSweep();
    });
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutations.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      if (trailing !== null) clearTimeout(trailing);
      window.removeEventListener("scroll", scheduleSweep);
      window.removeEventListener("resize", scheduleSweep);
      window.removeEventListener("hashchange", scheduleSweep);
    };
  }, []);
};
